import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  ComboboxItem,
  ComboboxItemGroup,
  Divider,
  Group,
  Loader,
  MultiSelect,
  OptionsFilter,
  Popover,
  Radio,
  ScrollArea,
  Select,
  Stack,
  Text
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconFilter, IconSearch } from '@tabler/icons-react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  useGetPractitionersByClinicIdQuery,
  useUpdatePagePreferencesMutation,
  useUpdateUserSettingsMutation
} from '@/store/slices/api/userApiSlice';
import { setFilters as setGlobalFilters } from '@/store/slices/preferencesSlice';
import { NULL_OPTION } from '@/utils/constants';
import {
  accountIdByClinicId,
  clinicsAfterAccountPick,
  isInternalPortalRole,
  practitionerClinicIdsFor,
  SELECT_CLINICS_FOR_PROVIDERS
} from '@/utils/globalFilterClinics';
import { useUserSettingsFilters } from '@/hooks/useUserSettingsFilters';
import pocStyles from '@/styles/pocScope.module.scss';

import { LabelAndValue } from '@/types/custom';
import { Filters } from '@/types/globalFilter';
import { DeviceType, DeviceVendor, getVendorDisplayName } from '@/types/matchDevice';
import { UserRole } from '@/types/user';
import LabelText from '@/ui/LabelText';

import styles from './FiltersButton.module.scss';

import type { UserSettings } from '@/store/slices/api/userApiSlice';

/**
 * The global filter (what the old header icon / FiltersModal edited). Values only; saved with
 * updateUserSettings exactly like FiltersModal. "Unassigned" is NULL_OPTION's value.
 */
interface GlobalDraft {
  clinics: string[];
  practitioners: string[];
  signingPractitioners: string[];
  deviceTypes: string[];
  vendors: string[];
}

const EMPTY_GLOBAL: GlobalDraft = {
  clinics: [],
  practitioners: [],
  signingPractitioners: [],
  deviceTypes: [],
  vendors: []
};

const UNASSIGNED = NULL_OPTION.value;
const UNASSIGNED_LABEL = 'Unassigned';
const NO_ACCOUNT = 'Other clinics';
/** Selected pills shown in a multi-select before "+N more" (see .compactPills). */
const MAX_PILLS = 2;

const DEVICE_TYPE_OPTIONS: LabelAndValue[] = [
  { label: UNASSIGNED_LABEL, value: UNASSIGNED },
  ...Object.values(DeviceType).map((deviceType) => ({ label: deviceType, value: deviceType }))
];

const VENDOR_OPTIONS: LabelAndValue[] = Object.values(DeviceVendor).map((vendor) => ({
  label: getVendorDisplayName(vendor),
  value: vendor
}));

const ADMIN_ROLES: (UserRole | undefined)[] = [
  UserRole.InternalSuperAdmin,
  UserRole.InternalAdmin,
  UserRole.InternalNonClinical,
  UserRole.InternalClinical
];

function withUnassigned(ids: string[], include?: boolean): string[] {
  return include && !ids.includes(UNASSIGNED) ? [...ids, UNASSIGNED] : ids;
}

function globalFromSettings(filters: Filters | null, settings?: UserSettings): GlobalDraft {
  if (!filters) return EMPTY_GLOBAL;
  const values = (items?: LabelAndValue[]) => (items ?? []).map((item) => item.value);
  return {
    clinics: values(filters.clinics),
    practitioners: withUnassigned(
      values(filters.practitioners),
      settings?.includeUnassignedPractitioner
    ),
    signingPractitioners: withUnassigned(
      values(filters.signingPractitioners),
      settings?.includeUnassignedSigningPractitioner
    ),
    deviceTypes: values(filters.deviceTypes),
    vendors: values(filters.vendors)
  };
}

function sameValues(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((value) => set.has(value));
}

function sameGlobal(a: GlobalDraft, b: GlobalDraft): boolean {
  return (Object.keys(EMPTY_GLOBAL) as (keyof GlobalDraft)[]).every((key) =>
    sameValues(a[key], b[key])
  );
}

/** Clinics grouped by account (parent), as in Add Patient's ClinicSelect. */
function groupClinics(clinics: UserSettings['clinics']): ComboboxItemGroup<ComboboxItem>[] {
  const groups = new Map<string, ComboboxItem[]>();
  clinics.forEach((clinic) => {
    const account = clinic.parent?.name?.trim() || NO_ACCOUNT;
    const items = groups.get(account) ?? [];
    items.push({ value: clinic.id, label: clinic.name });
    groups.set(account, items);
  });
  return [...groups.entries()]
    .sort(([a], [b]) => (a === NO_ACCOUNT ? 1 : b === NO_ACCOUNT ? -1 : a.localeCompare(b)))
    .map(([group, items]) => ({
      group,
      items: items.sort((a, b) => a.label.localeCompare(b.label))
    }));
}

/** Matches the clinic or the account name; an account match keeps all its clinics. */
const clinicFilter: OptionsFilter = ({ options, search }) => {
  const query = search.trim().toLowerCase();
  if (!query) return options;
  return (options as ComboboxItemGroup<ComboboxItem>[])
    .map((group) => {
      if (group.group.toLowerCase().includes(query)) return group;
      return {
        ...group,
        items: group.items.filter((item) => item.label.toLowerCase().includes(query))
      };
    })
    .filter((group) => group.items.length > 0);
};

/** Section with a small uppercase label, and Clear / Select all when those actions apply. */
const FilterSection: React.FC<{
  label: string;
  children: ReactNode;
  onClear?: () => void;
  onSelectAll?: () => void;
  hasValue?: boolean;
}> = ({ label, children, onClear, onSelectAll, hasValue }) => (
  <div className={styles.section}>
    <div className={styles.sectionHead}>
      <div className={styles.sectionLabel}>{label}</div>
      {(onSelectAll || (onClear && hasValue)) && (
        <span className={styles.sectionActions}>
          {onSelectAll && (
            <Anchor
              component="button"
              type="button"
              className={styles.sectionClear}
              aria-label={`Select all ${label}`}
              onClick={onSelectAll}>
              Select all
            </Anchor>
          )}
          {onClear && hasValue && (
            <Anchor
              component="button"
              type="button"
              className={styles.sectionClear}
              aria-label={`Clear ${label}`}
              onClick={onClear}>
              Clear
            </Anchor>
          )}
        </span>
      )}
    </div>
    {children}
  </div>
);

const MoreSelected: React.FC<{ count: number }> = ({ count }) =>
  count > MAX_PILLS ? (
    <Text size="xs" c="dimmed" mt={4}>
      +{count - MAX_PILLS} more
    </Text>
  ) : null;

const EmptyOptions: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Text size="xs" c="dimmed">
    {children}
  </Text>
);

const STATUS_OPTIONS: LabelAndValue[] = [
  { label: 'Active', value: 'Active' },
  { label: 'Pending', value: 'Candidate' },
  { label: 'Inactive', value: 'Inactive' },
  { label: 'Paused', value: 'Paused' }
];

const FILTER_OPTIONS = [
  { value: 'GREEN', label: 'Routine' },
  { value: 'YELLOW', label: 'Yellow Alert' },
  { value: 'RED', label: 'Red Alert' }
];

const REPORT_TYPE_OPTIONS: LabelAndValue[] = [
  { value: 'none', label: 'No selection' },
  { value: 'current', label: 'Most Recent Report' },
  { value: 'previous', label: 'Last Completed Report' }
];

interface FiltersButtonProps {
  selectedCurrentReportFilters?: string[];
  selectedPreviousReportFilters?: string[];
  isNonPractitionerSignedCRSelected?: boolean;
  isPendingReviewSelectedCRSelected?: boolean;
  isRecentSignedSelected?: boolean;
  isDownloadedSelected?: boolean;
  currentReportType?: 'current' | 'previous' | null;
  recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE';
  onApplyReportFilters?: (
    filters: string[],
    isNonPractitioner: boolean,
    isPendingReview: boolean,
    isRecentSigned: boolean,
    isNotDownloaded: boolean,
    reportType: 'current' | 'previous',
    recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE'
  ) => void;
}

export function FiltersButton({
  selectedCurrentReportFilters = [],
  selectedPreviousReportFilters = [],
  isNonPractitionerSignedCRSelected = false,
  isPendingReviewSelectedCRSelected = false,
  isRecentSignedSelected = false,
  isDownloadedSelected = false,
  currentReportType = null,
  recentSignedSortBy: initialRecentSignedSortBy = 'SIGNED_DATE',
  onApplyReportFilters
}: FiltersButtonProps) {
  const dispatch = useAppDispatch();
  const { filters: globalFilter, userSettingsId, userSettingsData } = useUserSettingsFilters();
  const [updateUserSettings, { isLoading: isUpdating }] = useUpdateUserSettingsMutation();
  const [updatePagePreferences] = useUpdatePagePreferencesMutation();
  const { user } = useAppSelector((state) => state.auth);
  const isUserAdmin = ADMIN_ROLES.includes(user?.role);

  const [opened, { open, close }] = useDisclosure(false);
  const isOpenRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  // Multi-select dropdowns render into the popover (not the body), so picking an option is not an
  // outside click and the list isn't clipped by the scroll area.
  const [dropdownEl, setDropdownEl] = useState<HTMLDivElement | null>(null);
  const comboboxProps = useMemo(
    () => ({
      withinPortal: true,
      portalProps: dropdownEl ? { target: dropdownEl } : undefined,
      zIndex: 1000
    }),
    [dropdownEl]
  );

  // --- Global filter (clinics, practitioners, device types, vendors) draft ---
  const committedGlobal = useMemo(
    () => globalFromSettings(globalFilter, userSettingsData),
    [globalFilter, userSettingsData]
  );
  const [draftGlobal, setDraftGlobal] = useState<GlobalDraft>(committedGlobal);

  const clinics = useMemo(() => userSettingsData?.clinics ?? [], [userSettingsData?.clinics]);
  const allClinicIds = useMemo(() => clinics.map((clinic) => clinic.id), [clinics]);
  const allClinicsSelected =
    allClinicIds.length > 0 && allClinicIds.every((id) => draftGlobal.clinics.includes(id));
  const clinicGroups = useMemo(() => groupClinics(clinics), [clinics]);
  const clinicAccount = useMemo(() => {
    const map = new Map<string, string>();
    clinics.forEach((clinic) => map.set(clinic.id, clinic.parent?.name?.trim() || NO_ACCOUNT));
    return map;
  }, [clinics]);
  const hasMultipleAccounts = useMemo(
    () => new Set(clinicAccount.values()).size > 1,
    [clinicAccount]
  );

  // One clinic, or several clinics that share a parent id, loads providers.
  // An internal selection across accounts does not.
  const clinicAccountIds = useMemo(() => accountIdByClinicId(clinics), [clinics]);
  const practitionerClinicIds = useMemo(
    () =>
      practitionerClinicIdsFor(
        draftGlobal.clinics,
        allClinicIds,
        isInternalPortalRole(user?.role),
        clinicAccountIds
      ),
    [draftGlobal.clinics, allClinicIds, user?.role, clinicAccountIds]
  );
  const { data: practitionerData, isFetching: isPractitionersFetching } =
    useGetPractitionersByClinicIdQuery(
      { clinicIds: practitionerClinicIds },
      { skip: !opened || practitionerClinicIds.length === 0 }
    );

  const practitionerOptions = useMemo<LabelAndValue[]>(() => {
    const labels = new Map<string, string>();
    practitionerData?.data.forEach((practitioner) =>
      labels.set(practitioner.id, practitioner.name)
    );
    // Keep already-saved selections labelled even before the practitioner query returns.
    [...(globalFilter?.practitioners ?? []), ...(globalFilter?.signingPractitioners ?? [])]
      .filter((item) => item.value !== UNASSIGNED && !labels.has(item.value))
      .forEach((item) => labels.set(item.value, item.label));
    const options = [...labels.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: UNASSIGNED, label: UNASSIGNED_LABEL }, ...options];
  }, [practitionerData, globalFilter?.practitioners, globalFilter?.signingPractitioners]);

  /** One account at a time when a single clinic from another account is added. Select-all stays. */
  const handleClinicsChange = (values: string[]) => {
    setDraftGlobal((prev) => ({
      ...prev,
      clinics: clinicsAfterAccountPick(
        prev.clinics,
        values,
        (id) => clinicAccount.get(id) ?? NO_ACCOUNT
      ),
      practitioners: [],
      signingPractitioners: []
    }));
  };

  const setGlobalField = (key: keyof GlobalDraft) => (values: string[]) =>
    setDraftGlobal((prev) => ({ ...prev, [key]: values }));

  // --- Draft state ---
  const defaultStatuses = useMemo<LabelAndValue[]>(() => {
    const statuses =
      userSettingsData?.patientListPagePreferences?.patientStatuses ??
      globalFilter?.patientStatuses ??
      [];

    return statuses.map((status) =>
      typeof status === 'string' ? { label: status, value: status } : status
    );
  }, [
    globalFilter?.patientStatuses,
    userSettingsData?.patientListPagePreferences?.patientStatuses
  ]);
  const [draftStatuses, setDraftStatuses] = useState<LabelAndValue[]>(defaultStatuses);

  const [reportType, setReportType] = useState<'current' | 'previous' | null>(currentReportType);
  const [tempCurrentReportFilters, setTempCurrentReportFilters] = useState<string[]>(
    selectedCurrentReportFilters
  );
  const [tempPreviousReportFilters, setTempPreviousReportFilters] = useState<string[]>(
    selectedPreviousReportFilters
  );
  const [tempNonPractitionerSelected, setTempNonPractitionerSelected] = useState(
    isNonPractitionerSignedCRSelected
  );
  const [tempPendingReviewSelected, setTempPendingReviewSelected] = useState(
    isPendingReviewSelectedCRSelected
  );
  const [tempRecentSignedSelected, setTempRecentSignedSelected] = useState(isRecentSignedSelected);
  const [tempDownloadedSelected, setTempDownloadedSelected] = useState(isDownloadedSelected);
  const [recentSignedSortBy, setRecentSignedSortBy] = useState<'SIGNED_DATE' | 'DATE_OF_SERVICE'>(
    initialRecentSignedSortBy
  );
  const [showRecentSignedSubmenu, setShowRecentSignedSubmenu] = useState(false);
  const [prevReportType, setPrevReportType] = useState<'current' | 'previous' | null>(
    currentReportType
  );

  // Initialize with user preferences when available
  useEffect(() => {
    if (userSettingsData?.patientListPagePreferences) {
      const prefs = userSettingsData.patientListPagePreferences;

      const hasCurrentPrefs =
        (prefs.currentReportFilters?.colors && prefs.currentReportFilters.colors.length > 0) ||
        prefs.currentReportFilters?.isNonPractitionerSigned === true ||
        prefs.currentReportFilters?.isPendingReview === true;

      const hasPreviousPrefs =
        (prefs.previousReportFilters?.colors && prefs.previousReportFilters.colors.length > 0) ||
        prefs.previousReportFilters?.isRecentSigned === true ||
        prefs.previousReportFilters?.isDownloaded === true ||
        prefs.previousReportFilters?.isNonPractitionerSigned === true;

      if (hasCurrentPrefs) {
        setReportType('current');
      } else if (hasPreviousPrefs) {
        setReportType('previous');
      } else {
        setReportType(null);
      }
      // Load current report filters
      if (prefs.currentReportFilters) {
        const currentFilters = prefs.currentReportFilters;
        if (currentFilters.colors) {
          setTempCurrentReportFilters(currentFilters.colors);
        }
        if (currentFilters.isPendingReview !== undefined) {
          setTempPendingReviewSelected(currentFilters.isPendingReview);
        }
      }

      // Load previous report filters
      if (prefs.previousReportFilters) {
        const previousFilters = prefs.previousReportFilters;
        if (previousFilters.colors) {
          setTempPreviousReportFilters(previousFilters.colors);
        }
        if (previousFilters.isRecentSigned !== undefined) {
          setTempRecentSignedSelected(previousFilters.isRecentSigned);
        }
        if (previousFilters.recentSignedSortBy !== undefined) {
          setRecentSignedSortBy(previousFilters.recentSignedSortBy);
        }
        if (previousFilters.isDownloaded !== undefined) {
          setTempDownloadedSelected(previousFilters.isDownloaded);
        }
        if (previousFilters.isNonPractitionerSigned !== undefined) {
          setTempNonPractitionerSelected(previousFilters.isNonPractitionerSigned);
        }
      }
    }
  }, [userSettingsData]);

  // Initialize draft status from user preferences or global filters
  useEffect(() => {
    if (userSettingsData?.patientListPagePreferences?.patientStatuses) {
      const statusValues = userSettingsData.patientListPagePreferences.patientStatuses;
      if (statusValues && statusValues.length > 0) {
        setDraftStatuses(STATUS_OPTIONS.filter((opt) => statusValues.includes(opt.value)));
      } else {
        setDraftStatuses([]);
      }
    } else {
      setDraftStatuses(globalFilter?.patientStatuses ?? []);
    }
  }, [userSettingsData, globalFilter?.patientStatuses]);

  // Sync external committed state → draft when closed
  useEffect(() => {
    if (!isOpenRef.current && userSettingsData?.patientListPagePreferences?.patientStatuses) {
      const statusValues = userSettingsData.patientListPagePreferences.patientStatuses;
      if (statusValues && statusValues.length > 0) {
        setDraftStatuses(STATUS_OPTIONS.filter((opt) => statusValues.includes(opt.value)));
      } else {
        setDraftStatuses(globalFilter?.patientStatuses ?? []);
      }
    } else if (!isOpenRef.current) {
      setDraftStatuses(globalFilter?.patientStatuses ?? []);
    }
  }, [
    globalFilter?.patientStatuses,
    userSettingsData?.patientListPagePreferences?.patientStatuses
  ]);

  // Reset state based on API response when report type changes
  useEffect(() => {
    if (userSettingsData?.patientListPagePreferences) {
      const prefs = userSettingsData.patientListPagePreferences;

      // Check if report type has changed by comparing with previous value
      if (prevReportType !== reportType && prevReportType !== null) {
        if (reportType === 'current') {
          // We switched to current - reset previous-specific fields
          setTempPreviousReportFilters([]);
          setTempRecentSignedSelected(false);
          setTempDownloadedSelected(false);
          setTempNonPractitionerSelected(false);
          setRecentSignedSortBy('SIGNED_DATE');
          setShowRecentSignedSubmenu(false);
        } else if (reportType === 'previous') {
          // We switched to previous - reset current-specific fields
          setTempCurrentReportFilters([]);
          setTempNonPractitionerSelected(false);
          setTempPendingReviewSelected(false);
        }

        // Update the previous report type after reset
        setPrevReportType(reportType);
      }

      // Now load the current values from API
      if (prefs.currentReportFilters && reportType === 'current') {
        const currentFilters = prefs.currentReportFilters;
        if (currentFilters.colors !== undefined) {
          setTempCurrentReportFilters(currentFilters.colors);
        }
        if (currentFilters.isPendingReview !== undefined) {
          setTempPendingReviewSelected(currentFilters.isPendingReview);
        }
      }

      if (prefs.previousReportFilters && reportType === 'previous') {
        const previousFilters = prefs.previousReportFilters;
        if (previousFilters.colors !== undefined) {
          setTempPreviousReportFilters(previousFilters.colors);
        }
        if (previousFilters.isRecentSigned !== undefined) {
          setTempRecentSignedSelected(previousFilters.isRecentSigned);
        }
        if (previousFilters.recentSignedSortBy !== undefined) {
          setRecentSignedSortBy(previousFilters.recentSignedSortBy);
        }
        if (previousFilters.isDownloaded !== undefined) {
          setTempDownloadedSelected(previousFilters.isDownloaded);
        }
        if (previousFilters.isNonPractitionerSigned !== undefined) {
          setTempNonPractitionerSelected(previousFilters.isNonPractitionerSigned);
        }
      }
    } else if (reportType !== prevReportType && prevReportType !== null) {
      // Fallback if no user settings data available
      if (reportType === 'current') {
        setTempPreviousReportFilters([]);
        setTempRecentSignedSelected(false);
        setTempDownloadedSelected(false);
        setRecentSignedSortBy('SIGNED_DATE');
        setShowRecentSignedSubmenu(false);
      } else if (reportType === 'previous') {
        setTempCurrentReportFilters([]);
        setTempNonPractitionerSelected(false);
        setTempPendingReviewSelected(false);
      }
      setPrevReportType(reportType);
    }
  }, [userSettingsData, reportType, prevReportType, currentReportType]);

  useEffect(() => {
    if (!isOpenRef.current) {
      // Only sync to props if user settings data is not available
      // or if we want to use the committed values instead of user preferences
      if (!userSettingsData?.patientListPagePreferences) {
        const hasCurrentFilters =
          selectedCurrentReportFilters.length > 0 || isPendingReviewSelectedCRSelected;
        const hasPreviousFilters =
          selectedPreviousReportFilters.length > 0 ||
          isRecentSignedSelected ||
          isDownloadedSelected ||
          isNonPractitionerSignedCRSelected;

        // Track previous report type to detect changes
        const prevType = prevReportType;
        const newReportType = hasCurrentFilters
          ? 'current'
          : hasPreviousFilters
            ? 'previous'
            : null;

        setReportType(newReportType);
        setTempCurrentReportFilters(selectedCurrentReportFilters);
        setTempPreviousReportFilters(selectedPreviousReportFilters);
        setTempNonPractitionerSelected(isNonPractitionerSignedCRSelected);
        setTempPendingReviewSelected(isPendingReviewSelectedCRSelected);
        setTempRecentSignedSelected(isRecentSignedSelected);
        setTempDownloadedSelected(isDownloadedSelected);
        setRecentSignedSortBy(initialRecentSignedSortBy);

        // Reset state if report type changed
        if (prevType !== null && prevType !== newReportType) {
          if (newReportType === 'current') {
            setTempPreviousReportFilters([]);
            setTempRecentSignedSelected(false);
            setTempDownloadedSelected(false);
            setRecentSignedSortBy('SIGNED_DATE');
            setShowRecentSignedSubmenu(false);
          } else if (newReportType === 'previous') {
            setTempCurrentReportFilters([]);
            setTempNonPractitionerSelected(false);
            setTempPendingReviewSelected(false);
          }
        }

        setPrevReportType(newReportType);
      }
    }
  }, [
    selectedCurrentReportFilters,
    selectedPreviousReportFilters,
    isNonPractitionerSignedCRSelected,
    isPendingReviewSelectedCRSelected,
    isRecentSignedSelected,
    isDownloadedSelected,
    initialRecentSignedSortBy,
    userSettingsData,
    prevReportType,
    currentReportType
  ]);

  const handleOpen = () => {
    isOpenRef.current = true;
    // Edits are staged from the saved global filter each time the popover opens.
    setDraftGlobal(committedGlobal);
    open();
  };

  const handleClose = useCallback(() => {
    isOpenRef.current = false;
    close();

    // Only sync to props if user settings data is not available
    if (!userSettingsData?.patientListPagePreferences) {
      const hasCurrentFilters =
        selectedCurrentReportFilters.length > 0 || isPendingReviewSelectedCRSelected;
      const hasPreviousFilters =
        selectedPreviousReportFilters.length > 0 ||
        isRecentSignedSelected ||
        isDownloadedSelected ||
        isNonPractitionerSignedCRSelected;

      setDraftStatuses(globalFilter?.patientStatuses ?? []);
      setReportType(hasCurrentFilters ? 'current' : hasPreviousFilters ? 'previous' : null);
      setTempCurrentReportFilters(selectedCurrentReportFilters);
      setTempPreviousReportFilters(selectedPreviousReportFilters);
      setTempNonPractitionerSelected(isNonPractitionerSignedCRSelected);
      setTempPendingReviewSelected(isPendingReviewSelectedCRSelected);
      setTempRecentSignedSelected(isRecentSignedSelected);
      setTempDownloadedSelected(isDownloadedSelected);
      setRecentSignedSortBy(initialRecentSignedSortBy);
      setShowRecentSignedSubmenu(false);
    }
  }, [
    close,
    globalFilter?.patientStatuses,
    selectedCurrentReportFilters,
    selectedPreviousReportFilters,
    isNonPractitionerSignedCRSelected,
    isPendingReviewSelectedCRSelected,
    isRecentSignedSelected,
    isDownloadedSelected,
    initialRecentSignedSortBy,
    userSettingsData
  ]);

  // Staged like every other edit: nothing is saved until Apply.
  const clearReportDraft = () => {
    setReportType(null);
    setTempCurrentReportFilters([]);
    setTempPreviousReportFilters([]);
    setTempNonPractitionerSelected(false);
    setTempPendingReviewSelected(false);
    setTempRecentSignedSelected(false);
    setTempDownloadedSelected(false);
    setRecentSignedSortBy('SIGNED_DATE');
    setShowRecentSignedSubmenu(false);
  };

  const handleClearAll = () => {
    // No clinics/practitioners/types/vendors means all; no "Unassigned" means the
    // include-unassigned flags go back to false.
    setDraftGlobal(EMPTY_GLOBAL);
    setDraftStatuses([]);
    setReportType(null);
    setTempCurrentReportFilters([]);
    setTempPreviousReportFilters([]);
    setTempNonPractitionerSelected(false);
    setTempPendingReviewSelected(false);
    setTempRecentSignedSelected(false);
    setTempDownloadedSelected(false);
    setRecentSignedSortBy('SIGNED_DATE');
  };

  const handleReportFilterToggle = useCallback(
    (value: string) => {
      if (reportType === 'current') {
        setTempCurrentReportFilters((prev) =>
          prev.includes(value) ? prev.filter((f) => f !== value) : [...prev, value]
        );
      } else if (reportType === 'previous') {
        setTempPreviousReportFilters((prev) =>
          prev.includes(value) ? prev.filter((f) => f !== value) : [...prev, value]
        );
      }
    },
    [reportType]
  );

  const activeReportFilters =
    reportType === 'current' ? tempCurrentReportFilters : tempPreviousReportFilters;

  const isAllReportFiltersSelected = useMemo(() => {
    if (!reportType) return false;
    const allBase = FILTER_OPTIONS.every((opt) => activeReportFilters.includes(opt.value));
    if (reportType === 'current') {
      return allBase && tempPendingReviewSelected;
    }
    return (
      allBase && tempRecentSignedSelected && tempDownloadedSelected && tempNonPractitionerSelected
    );
  }, [
    reportType,
    activeReportFilters,
    tempNonPractitionerSelected,
    tempPendingReviewSelected,
    tempRecentSignedSelected,
    tempDownloadedSelected
  ]);

  const handleSelectAllToggle = useCallback(() => {
    if (!reportType) return;
    if (isAllReportFiltersSelected) {
      if (reportType === 'current') {
        setTempCurrentReportFilters([]);
        setTempPendingReviewSelected(false);
      } else {
        setTempPreviousReportFilters([]);
        setTempRecentSignedSelected(false);
        setTempDownloadedSelected(false);
        setTempNonPractitionerSelected(false);
      }
    } else {
      if (reportType === 'current') {
        setTempCurrentReportFilters(FILTER_OPTIONS.map((o) => o.value));
        setTempPendingReviewSelected(true);
      } else {
        setTempPreviousReportFilters(FILTER_OPTIONS.map((o) => o.value));
        setTempRecentSignedSelected(true);
        setTempDownloadedSelected(true);
        setTempNonPractitionerSelected(true);
      }
    }
  }, [reportType, isAllReportFiltersSelected]);

  const handleApply = useCallback(async () => {
    setIsSaving(true);
    try {
      if (userSettingsId) {
        // Global filter: same mutation and payload as the old FiltersModal ("Unassigned" is
        // sent as the include-unassigned flags, not as an id).
        await updateUserSettings({
          settingsId: userSettingsId,
          organizationIds: draftGlobal.clinics,
          practitionerIds: draftGlobal.practitioners.filter((id) => id !== UNASSIGNED),
          signingPractitionerIds: draftGlobal.signingPractitioners.filter(
            (id) => id !== UNASSIGNED
          ),
          deviceTypes: draftGlobal.deviceTypes,
          vendors: draftGlobal.vendors,
          includeUnassignedPractitioner: draftGlobal.practitioners.includes(UNASSIGNED),
          includeUnassignedSigningPractitioner:
            draftGlobal.signingPractitioners.includes(UNASSIGNED)
        }).unwrap();

        // Store prev report type before updating
        const prevType = reportType;

        // Update page preferences to the new endpoint
        // This includes columns, density and patientStatuses as requested
        await updatePagePreferences({
          settingsId: userSettingsId,
          patientListPagePreferences: {
            patientStatuses: draftStatuses.map((s) => s.value),
            density: undefined,
            columns: undefined,
            reportType: reportType,
            currentReportFilters:
              reportType === 'current'
                ? {
                    colors: tempCurrentReportFilters,
                    isPendingReview: tempPendingReviewSelected
                  }
                : {},
            previousReportFilters:
              reportType === 'previous'
                ? {
                    colors: tempPreviousReportFilters,
                    isRecentSigned: tempRecentSignedSelected,
                    recentSignedSortBy: recentSignedSortBy,
                    isDownloaded: tempDownloadedSelected,
                    isNonPractitionerSigned: tempNonPractitionerSelected
                  }
                : {}
          }
        }).unwrap();

        // Clear state for the other tab after successful update
        if (prevType !== reportType) {
          if (reportType === 'current') {
            setTempPreviousReportFilters([]);
            setTempRecentSignedSelected(false);
            setTempDownloadedSelected(false);
            setRecentSignedSortBy('SIGNED_DATE');
            setShowRecentSignedSubmenu(false);
          } else if (reportType === 'previous') {
            setTempCurrentReportFilters([]);
            setTempNonPractitionerSelected(false);
            setTempPendingReviewSelected(false);
          }
          setPrevReportType(reportType);
        }
      }

      const pick = (options: LabelAndValue[], values: string[]) =>
        values.map(
          (value) => options.find((option) => option.value === value) ?? { label: value, value }
        );
      const clinicOptions = clinics.map((clinic) => ({ label: clinic.name, value: clinic.id }));
      dispatch(
        setGlobalFilters({
          clinics: pick(clinicOptions, draftGlobal.clinics),
          practitioners: pick(practitionerOptions, draftGlobal.practitioners),
          signingPractitioners: pick(practitionerOptions, draftGlobal.signingPractitioners),
          deviceTypes: pick(DEVICE_TYPE_OPTIONS, draftGlobal.deviceTypes),
          vendors: pick(VENDOR_OPTIONS, draftGlobal.vendors),
          patientStatuses: draftStatuses
        })
      );

      if (onApplyReportFilters) {
        if (reportType === 'current') {
          onApplyReportFilters(
            tempCurrentReportFilters,
            false,
            tempPendingReviewSelected,
            false,
            false,
            'current'
          );
        } else if (reportType === 'previous') {
          onApplyReportFilters(
            tempPreviousReportFilters,
            tempNonPractitionerSelected,
            false,
            tempRecentSignedSelected,
            tempDownloadedSelected,
            'previous',
            tempRecentSignedSelected ? recentSignedSortBy : undefined
          );
        } else {
          onApplyReportFilters([], false, false, false, false, 'current');
        }
      }

      isOpenRef.current = false;
      close();
    } catch {
      showNotification({ title: 'Error', message: 'Failed to update filters', color: 'red' });
    } finally {
      setIsSaving(false);
    }
  }, [
    updateUserSettings,
    updatePagePreferences,
    userSettingsId,
    dispatch,
    draftGlobal,
    clinics,
    practitionerOptions,
    draftStatuses,
    onApplyReportFilters,
    reportType,
    tempCurrentReportFilters,
    tempPreviousReportFilters,
    tempNonPractitionerSelected,
    tempPendingReviewSelected,
    tempRecentSignedSelected,
    tempDownloadedSelected,
    recentSignedSortBy,
    close
  ]);

  const isApplyDisabled = useMemo(() => {
    const globalChanged = !sameGlobal(draftGlobal, committedGlobal);
    const statusChanged = JSON.stringify(draftStatuses) !== JSON.stringify(defaultStatuses);
    const reportChanged =
      (reportType !== currentReportType && reportType == null) ||
      JSON.stringify(tempCurrentReportFilters) !== JSON.stringify(selectedCurrentReportFilters) ||
      JSON.stringify(tempPreviousReportFilters) !== JSON.stringify(selectedPreviousReportFilters) ||
      tempNonPractitionerSelected !== isNonPractitionerSignedCRSelected ||
      tempPendingReviewSelected !== isPendingReviewSelectedCRSelected ||
      tempRecentSignedSelected !== isRecentSignedSelected ||
      tempDownloadedSelected !== isDownloadedSelected ||
      recentSignedSortBy !== initialRecentSignedSortBy;
    return !globalChanged && !statusChanged && !reportChanged;
  }, [
    draftGlobal,
    committedGlobal,
    draftStatuses,
    defaultStatuses,
    reportType,
    currentReportType,
    tempCurrentReportFilters,
    selectedCurrentReportFilters,
    tempPreviousReportFilters,
    selectedPreviousReportFilters,
    tempNonPractitionerSelected,
    isNonPractitionerSignedCRSelected,
    tempPendingReviewSelected,
    isPendingReviewSelectedCRSelected,
    tempRecentSignedSelected,
    isRecentSignedSelected,
    tempDownloadedSelected,
    isDownloadedSelected,
    recentSignedSortBy,
    initialRecentSignedSortBy
  ]);

  const isReportDraftEmpty =
    reportType === null &&
    tempCurrentReportFilters.length === 0 &&
    tempPreviousReportFilters.length === 0 &&
    !tempNonPractitionerSelected &&
    !tempPendingReviewSelected &&
    !tempRecentSignedSelected &&
    !tempDownloadedSelected;

  const isClearAllDisabled =
    sameGlobal(draftGlobal, EMPTY_GLOBAL) && draftStatuses.length === 0 && isReportDraftEmpty;

  // Badge: number of active filter groups (each global field, patient status, report filter).
  const activeCount = useMemo(() => {
    let count = (Object.keys(EMPTY_GLOBAL) as (keyof GlobalDraft)[]).filter(
      (key) => committedGlobal[key].length > 0
    ).length;
    if ((globalFilter?.patientStatuses?.length ?? 0) > 0) count += 1;
    const hasCurrentFilters =
      selectedCurrentReportFilters.length > 0 ||
      isNonPractitionerSignedCRSelected ||
      isPendingReviewSelectedCRSelected;
    const hasPreviousFilters =
      selectedPreviousReportFilters.length > 0 || isRecentSignedSelected || isDownloadedSelected;
    if (hasCurrentFilters || hasPreviousFilters) count += 1;
    return count;
  }, [
    committedGlobal,
    globalFilter?.patientStatuses,
    selectedCurrentReportFilters,
    selectedPreviousReportFilters,
    isNonPractitionerSignedCRSelected,
    isPendingReviewSelectedCRSelected,
    isRecentSignedSelected,
    isDownloadedSelected
  ]);

  return (
    <Popover
      opened={opened}
      onChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      position="bottom-end"
      shadow="md"
      radius={12}
      offset={6}
      width={380}
      trapFocus
      returnFocus
      closeOnEscape
      withinPortal>
      <Popover.Target>
        <Button
          variant="default"
          leftSection={<IconFilter size={16} />}
          rightSection={
            activeCount > 0 ? (
              <Badge size="sm" circle variant="filled" color="plum.7">
                {activeCount}
              </Badge>
            ) : null
          }
          size="sm"
          onClick={opened ? handleClose : handleOpen}>
          Filters
        </Button>
      </Popover.Target>

      {/* Portalled, so it carries the POC scope itself. */}
      <Popover.Dropdown
        ref={setDropdownEl}
        p={0}
        className={`${pocStyles.pocScope} ${styles.dropdown}`}>
        <div className={styles.header}>
          <span>Filters</span>
          <Anchor
            component="button"
            type="button"
            className={styles.clearAll}
            disabled={isClearAllDisabled}
            onClick={handleClearAll}>
            Clear all
          </Anchor>
        </div>

        <ScrollArea.Autosize mah={520} type="auto">
          <Stack gap={0}>
            <FilterSection
              label="Clinics"
              hasValue={draftGlobal.clinics.length > 0}
              onClear={() => handleClinicsChange([])}
              onSelectAll={
                isUserAdmin && !allClinicsSelected
                  ? () => handleClinicsChange(allClinicIds)
                  : undefined
              }>
              {clinics.length === 0 ? (
                <EmptyOptions>No clinics available</EmptyOptions>
              ) : (
                <>
                  <MultiSelect
                    size="xs"
                    aria-label="Clinics"
                    className={styles.compactPills}
                    placeholder={
                      draftGlobal.clinics.length ? undefined : 'Search clinic or account'
                    }
                    leftSection={<IconSearch size={13} />}
                    data={clinicGroups}
                    value={draftGlobal.clinics}
                    onChange={handleClinicsChange}
                    filter={clinicFilter}
                    searchable
                    clearable
                    nothingFoundMessage="No clinic matches"
                    maxDropdownHeight={260}
                    comboboxProps={comboboxProps}
                  />
                  <MoreSelected count={draftGlobal.clinics.length} />
                  {hasMultipleAccounts && (
                    <Text size="xs" c="dimmed" mt={4}>
                      Picking a clinic in another account resets your selection.
                    </Text>
                  )}
                </>
              )}
            </FilterSection>

            <FilterSection
              label="Following practitioner"
              hasValue={draftGlobal.practitioners.length > 0}
              onClear={() => setGlobalField('practitioners')([])}>
              <MultiSelect
                key={`following-${draftGlobal.clinics.join(',')}`}
                size="xs"
                aria-label="Following practitioner"
                className={styles.compactPills}
                placeholder={
                  draftGlobal.practitioners.length
                    ? undefined
                    : practitionerClinicIds.length > 0
                      ? 'Search practitioners'
                      : SELECT_CLINICS_FOR_PROVIDERS
                }
                leftSection={<IconSearch size={13} />}
                rightSection={isPractitionersFetching ? <Loader size={12} /> : undefined}
                data={practitionerOptions}
                value={draftGlobal.practitioners}
                onChange={setGlobalField('practitioners')}
                searchable
                clearable
                nothingFoundMessage="No practitioners for the selected clinics"
                maxDropdownHeight={240}
                comboboxProps={comboboxProps}
              />
              <MoreSelected count={draftGlobal.practitioners.length} />
            </FilterSection>

            <FilterSection
              label="Signing practitioner"
              hasValue={draftGlobal.signingPractitioners.length > 0}
              onClear={() => setGlobalField('signingPractitioners')([])}>
              <MultiSelect
                key={`signing-${draftGlobal.clinics.join(',')}`}
                size="xs"
                aria-label="Signing practitioner"
                className={styles.compactPills}
                placeholder={
                  draftGlobal.signingPractitioners.length
                    ? undefined
                    : practitionerClinicIds.length > 0
                      ? 'Search practitioners'
                      : SELECT_CLINICS_FOR_PROVIDERS
                }
                leftSection={<IconSearch size={13} />}
                rightSection={isPractitionersFetching ? <Loader size={12} /> : undefined}
                data={practitionerOptions}
                value={draftGlobal.signingPractitioners}
                onChange={setGlobalField('signingPractitioners')}
                searchable
                clearable
                nothingFoundMessage="No practitioners for the selected clinics"
                maxDropdownHeight={240}
                comboboxProps={comboboxProps}
              />
              <MoreSelected count={draftGlobal.signingPractitioners.length} />
            </FilterSection>

            <FilterSection
              label="Device type"
              hasValue={draftGlobal.deviceTypes.length > 0}
              onClear={() => setGlobalField('deviceTypes')([])}>
              <Chip.Group
                multiple
                value={draftGlobal.deviceTypes}
                onChange={setGlobalField('deviceTypes')}>
                <Group gap={6}>
                  {DEVICE_TYPE_OPTIONS.map((option) => (
                    <Chip key={option.value} value={option.value} size="xs" color="plum.9">
                      {option.label}
                    </Chip>
                  ))}
                </Group>
              </Chip.Group>
            </FilterSection>

            <FilterSection
              label="Manufacturer"
              hasValue={draftGlobal.vendors.length > 0}
              onClear={() => setGlobalField('vendors')([])}>
              <Chip.Group multiple value={draftGlobal.vendors} onChange={setGlobalField('vendors')}>
                <Group gap={6}>
                  {VENDOR_OPTIONS.map((option) => (
                    <Chip key={option.value} value={option.value} size="xs" color="plum.9">
                      {option.label}
                    </Chip>
                  ))}
                </Group>
              </Chip.Group>
            </FilterSection>

            <FilterSection
              label="Patient status"
              hasValue={draftStatuses.length > 0}
              onClear={() => setDraftStatuses([])}>
              <Chip.Group
                multiple
                value={draftStatuses.map((status) => status.value)}
                onChange={(values) =>
                  setDraftStatuses(STATUS_OPTIONS.filter((opt) => values.includes(opt.value)))
                }>
                <Group gap={6}>
                  {STATUS_OPTIONS.map((option) => (
                    <Chip key={option.value} value={option.value} size="xs" color="plum.9">
                      {option.label}
                    </Chip>
                  ))}
                </Group>
              </Chip.Group>
            </FilterSection>

            {/* Report Type: same Mantine control as the fields above; same values as before. */}
            <FilterSection
              label="Report type"
              hasValue={!isReportDraftEmpty}
              onClear={clearReportDraft}>
              <Select
                size="xs"
                aria-label="Report type"
                data={REPORT_TYPE_OPTIONS}
                value={reportType ?? 'none'}
                onChange={(val) =>
                  setReportType(!val || val === 'none' ? null : (val as 'current' | 'previous'))
                }
                allowDeselect={false}
                comboboxProps={{
                  ...comboboxProps,
                  classNames: { dropdown: pocStyles.pocScope }
                }}
              />
            </FilterSection>

            {reportType && (
              <div className={styles.section}>
                <Group justify="space-between" mb="xs">
                  <LabelText
                    label={
                      reportType === 'current' ? 'Most Recent Report' : 'Last Completed Report'
                    }
                  />
                  <Button size="xs" variant="subtle" color="plum" onClick={handleSelectAllToggle}>
                    {isAllReportFiltersSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                </Group>

                <Divider mx={-16} mb="xs" />

                <Stack gap="xs">
                  {FILTER_OPTIONS.map(({ value, label }) => (
                    <Box key={value}>
                      <Checkbox
                        checked={activeReportFilters.includes(value)}
                        onChange={() => handleReportFilterToggle(value)}
                        label={label}
                        mb="xs"
                      />
                      <Divider />
                    </Box>
                  ))}

                  {reportType === 'current' && (
                    <Box>
                      <Checkbox
                        checked={tempPendingReviewSelected}
                        onChange={() => setTempPendingReviewSelected((p) => !p)}
                        label="Pending Alert Review"
                        mb="xs"
                      />
                      <Divider />
                    </Box>
                  )}

                  {reportType === 'previous' && (
                    <>
                      <Box>
                        <Checkbox
                          checked={tempNonPractitionerSelected}
                          onChange={() => setTempNonPractitionerSelected((p) => !p)}
                          label="Pending Physician Review"
                          mb="xs"
                        />
                        <Divider />
                      </Box>
                      <Box>
                        <Group justify="space-between" wrap="nowrap">
                          <Checkbox
                            checked={tempRecentSignedSelected}
                            onChange={() => setTempRecentSignedSelected((p) => !p)}
                            label="Recently Signed (Last 30 days)"
                            mb="xs"
                            styles={{ label: { flex: 1 } }}
                          />
                          {tempRecentSignedSelected && (
                            <Box
                              onClick={() => setShowRecentSignedSubmenu((p) => !p)}
                              style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '4px',
                                marginTop: '-6px'
                              }}>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                style={{
                                  transform: showRecentSignedSubmenu
                                    ? 'rotate(90deg)'
                                    : 'rotate(0deg)',
                                  transition: 'transform 0.2s'
                                }}>
                                <polyline points="9 18 15 12 9 6" />
                              </svg>
                            </Box>
                          )}
                        </Group>
                        <Divider />
                        <Collapse in={tempRecentSignedSelected && showRecentSignedSubmenu}>
                          <Box pl="lg" py="xs">
                            <Radio.Group
                              value={recentSignedSortBy}
                              onChange={(v) =>
                                setRecentSignedSortBy(v as 'SIGNED_DATE' | 'DATE_OF_SERVICE')
                              }>
                              <Stack gap="xs">
                                <Radio value="SIGNED_DATE" label="Signed Date" />
                                <Divider />
                                <Radio value="DATE_OF_SERVICE" label="Date of Service" />
                              </Stack>
                            </Radio.Group>
                          </Box>
                        </Collapse>
                      </Box>
                      <Box>
                        <Checkbox
                          checked={tempDownloadedSelected}
                          onChange={() => setTempDownloadedSelected((p) => !p)}
                          label="Not downloaded"
                        />
                      </Box>
                    </>
                  )}
                </Stack>
              </div>
            )}
          </Stack>
        </ScrollArea.Autosize>

        <div className={styles.footer}>
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            disabled={isClearAllDisabled || isSaving}
            onClick={handleClearAll}>
            Clear all
          </Button>
          <Group gap={8}>
            <Button variant="default" size="xs" onClick={handleClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              size="xs"
              color="plum.9"
              loading={isSaving || isUpdating}
              disabled={isApplyDisabled}
              onClick={handleApply}>
              Apply
            </Button>
          </Group>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
